// @oasis-permission-public: Sam decided on 2026-10-23 that every Oasis user can edit Mirage MCP skills
import { KnownApiError } from "@mirage-web/networking/known-api-error";
import { skipToken } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, FilePlus2, RefreshCw, Save, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { McpCodeEditor } from "~/modules/mcp-skills/components/code-editor";
import {
  checkUploadAgainstLimits,
  defaultUploadPath,
  fileToBase64,
  formatBytes,
  formatDateTime,
  getMutationErrorMessage,
  isEditLockConflict,
  isImageContentType,
  validateSkillFilePath,
} from "~/modules/mcp-skills/helpers";
import { languageForPath } from "~/modules/mcp-skills/highlight";
import {
  deleteMcpSkillFileMutation,
  deleteMcpSkillMutation,
  saveMcpSkillFileMutation,
} from "~/modules/mcp-skills/mutations";
import { mcpSkillFileQuery, mcpSkillsQuery } from "~/modules/mcp-skills/queries";
import type { McpSkill, McpSkillLimits } from "~/modules/mcp-skills/schemas";

export const Route = createFileRoute("/_dash/mcp/skills/$skill")({
  component: RouteComponent,
});

function RouteComponent() {
  const { skill } = Route.useParams();
  const navigate = useNavigate();
  const { data, error, isLoading } = mcpSkillsQuery.useQuery();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [addFileOpen, setAddFileOpen] = useState(false);
  const [deleteSkillOpen, setDeleteSkillOpen] = useState(false);
  const { mutateAsync: deleteSkill, isPending: isDeletingSkill } =
    deleteMcpSkillMutation.useMutation();

  const skillData = data?.skills.find((entry) => entry.name === skill) ?? null;

  async function handleDeleteSkill() {
    try {
      await deleteSkill({ pathParams: { skill }, params: {} });
      toast.success(`Skill ${skill} deleted.`);
      void navigate({ to: "/mcp" });
    } catch (mutationError) {
      toast.error(getMutationErrorMessage(mutationError, "Failed to delete the skill."));
    }
  }

  return (
    <main className="space-y-6 pb-12">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Button variant="outline" size="sm" asChild>
            <Link to="/mcp">
              <ArrowLeft className="mr-2 size-4" /> MCP skills
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">{skill}</h1>
          {skillData?.description && (
            <p className="text-sm text-muted-foreground">{skillData.description}</p>
          )}
          {skillData?.error && <p className="text-sm text-destructive">{skillData.error}</p>}
        </div>
        {skillData && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAddFileOpen(true)}>
              <FilePlus2 className="mr-2 size-4" /> Add file
            </Button>
            <Button variant="destructive" onClick={() => setDeleteSkillOpen(true)}>
              <Trash2 className="mr-2 size-4" /> Delete skill
            </Button>
          </div>
        )}
      </div>

      <p className="rounded-md border bg-muted px-4 py-3 text-sm text-muted-foreground">
        Changes take effect after api_gateway restarts.
      </p>

      {isLoading && <p className="text-sm text-muted-foreground">Loading skill…</p>}
      {error && <p className="text-sm text-destructive">{error.message}</p>}
      {data && !skillData && (
        <p className="text-sm text-destructive">
          Skill {skill} was not found.{" "}
          <Link to="/mcp" className="underline">
            Back to the skill list
          </Link>
        </p>
      )}

      {skillData && data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Files</CardTitle>
              <CardDescription>
                {skillData.fileCount} of {skillData.limits.maxFiles} files,{" "}
                {formatBytes(skillData.totalBytes)} of {formatBytes(skillData.limits.maxTotalBytes)}
                .
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FileTable skill={skillData} selectedPath={selectedPath} onSelect={setSelectedPath} />
            </CardContent>
          </Card>

          {selectedPath && (
            <SelectedFilePanel
              skill={skill}
              path={selectedPath}
              skillData={skillData}
              onDeselect={() => setSelectedPath(null)}
            />
          )}
        </>
      )}

      <AddFileDialog
        skill={skill}
        skillData={skillData}
        limits={skillData?.limits ?? null}
        open={addFileOpen}
        onOpenChange={setAddFileOpen}
      />

      <Dialog open={deleteSkillOpen} onOpenChange={setDeleteSkillOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete skill {skill}?</DialogTitle>
            <DialogDescription>
              This deletes all {skillData?.fileCount ?? 0} files in the bucket under {skill}/. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteSkillOpen(false)}
              disabled={isDeletingSkill}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteSkill()}
              disabled={isDeletingSkill}
            >
              {isDeletingSkill ? "Deleting…" : "Delete skill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function FileTable({
  skill,
  selectedPath,
  onSelect,
}: {
  skill: McpSkill;
  selectedPath: string | null;
  onSelect: (path: string | null) => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState<{
    path: string;
    generation: string | null;
  } | null>(null);
  const { mutateAsync: deleteFile, isPending: isDeletingFile } =
    deleteMcpSkillFileMutation.useMutation();

  async function handleDeleteFile(path: string, generation: string | null) {
    if (generation == null) {
      toast.error("The file generation is unknown. Reload the skill and try again.");
      setConfirmDelete(null);
      return;
    }
    try {
      await deleteFile({
        pathParams: { skill: skill.name },
        params: { path, expectedGeneration: generation },
      });
      toast.success(`Deleted ${path}.`);
      if (selectedPath === path) {
        onSelect(null);
      }
    } catch (mutationError) {
      toast.error(getMutationErrorMessage(mutationError, "Failed to delete the file."));
    } finally {
      setConfirmDelete(null);
    }
  }

  async function handleCopyUri(uri: string) {
    try {
      await navigator.clipboard.writeText(uri);
      toast.success("Copied skill:// URI");
    } catch {
      toast.error("Failed to copy the URI.");
    }
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Path</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Size</TableHead>
            <TableHead>Last change</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {skill.files.map((file) => (
            <TableRow
              key={file.path}
              className={selectedPath === file.path ? "bg-muted/50" : undefined}
            >
              <TableCell>
                <button
                  type="button"
                  className="font-medium underline-offset-4 hover:underline"
                  onClick={() => onSelect(selectedPath === file.path ? null : file.path)}
                >
                  {file.path}
                </button>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{file.contentType ?? "—"}</Badge>
              </TableCell>
              <TableCell>{file.size != null ? formatBytes(file.size) : "—"}</TableCell>
              <TableCell>
                <span className="text-sm text-muted-foreground">
                  {formatDateTime(file.updatedAt)}
                  {file.updatedBy ? ` by ${file.updatedBy}` : ""}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => void handleCopyUri(file.uri)}>
                    Copy URI
                  </Button>
                  {file.path !== "SKILL.md" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() =>
                        setConfirmDelete({ path: file.path, generation: file.generation })
                      }
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {confirmDelete?.path}?</DialogTitle>
            <DialogDescription>
              This file is removed from the bucket. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              disabled={isDeletingFile}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmDelete && void handleDeleteFile(confirmDelete.path, confirmDelete.generation)
              }
              disabled={isDeletingFile}
            >
              {isDeletingFile ? "Deleting…" : "Delete file"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SelectedFilePanel({
  skill,
  path,
  skillData,
  onDeselect,
}: {
  skill: string;
  path: string;
  skillData: McpSkill;
  onDeselect: () => void;
}) {
  const { data, error, isLoading, refetch } = mcpSkillFileQuery.useQuery(
    path ? { pathParams: { skill }, params: { path } } : skipToken
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex min-w-0 items-center gap-2 font-mono text-base">
            <span className="truncate">{path}</span>
            {data?.text != null && (
              <Badge variant="secondary" className="font-normal lowercase">
                {languageForPath(path)}
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onDeselect}>
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && <p className="text-sm text-muted-foreground">Loading file…</p>}
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        {data &&
          (data.text != null ? (
            <TextFileEditor
              key={`${path}:${data.generation}`}
              skill={skill}
              path={path}
              text={data.text}
              generation={data.generation}
              onReload={() => void refetch()}
            />
          ) : (
            <BinaryFileEditor
              skill={skill}
              path={path}
              contentType={data.contentType}
              size={data.size}
              base64={data.base64 ?? null}
              generation={data.generation}
              limits={skillData.limits}
              currentFileCount={skillData.fileCount}
              currentTotalBytes={skillData.totalBytes}
            />
          ))}
      </CardContent>
    </Card>
  );
}

function TextFileEditor({
  skill,
  path,
  text,
  generation,
  onReload,
}: {
  skill: string;
  path: string;
  text: string;
  generation: string | null;
  onReload: () => void;
}) {
  const [value, setValue] = useState(text);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { mutateAsync: saveFile, isPending } = saveMcpSkillFileMutation.useMutation();

  async function handleSave() {
    setSaveError(null);
    if (generation == null) {
      setSaveError("The file generation is unknown. Reload the skill.");
      return;
    }
    try {
      await saveFile({
        pathParams: { skill },
        params: { path },
        body: { text: value, expectedGeneration: generation },
      });
      toast.success(`Saved ${path}.`);
    } catch (mutationError) {
      if (mutationError instanceof KnownApiError && mutationError.responseStatus === 409) {
        setSaveError("This file changed after you opened it. Reload it.");
        return;
      }
      setSaveError(getMutationErrorMessage(mutationError, "Failed to save the file."));
    }
  }

  return (
    <div className="space-y-3">
      <McpCodeEditor
        value={value}
        onChange={(nextValue) => {
          setValue(nextValue);
          setSaveError(null);
        }}
        language={languageForPath(path)}
        minRows={12}
        ariaLabel={`Edit ${path}`}
      />
      {saveError && (
        <div className="flex items-center justify-between gap-2 text-sm text-destructive">
          <span>{saveError}</span>
          <Button variant="outline" size="sm" onClick={onReload}>
            <RefreshCw className="mr-2 size-4" /> Reload
          </Button>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button
          onClick={() => void handleSave()}
          disabled={isPending || value === text || generation == null}
        >
          <Save className="mr-2 size-4" /> {isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function BinaryFileEditor({
  skill,
  path,
  contentType,
  size,
  base64,
  generation,
  limits,
  currentFileCount,
  currentTotalBytes,
}: {
  skill: string;
  path: string;
  contentType: string | null;
  size: number | null;
  base64: string | null;
  generation: string | null;
  limits: McpSkillLimits;
  currentFileCount: number;
  currentTotalBytes: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [replaceError, setReplaceError] = useState<string | null>(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const { mutateAsync: saveFile } = saveMcpSkillFileMutation.useMutation();

  async function handleReplaceFile(file: File) {
    setReplaceError(null);
    setIsReplacing(true);
    try {
      if (generation == null) {
        setReplaceError("The file generation is unknown. Reload the skill.");
        return;
      }
      // Check the limits before reading the file: a huge selection must not
      // allocate an ArrayBuffer plus base64 before the server would refuse it.
      const limitError = checkUploadAgainstLimits({
        limits,
        currentFileCount,
        currentTotalBytes,
        newFileSize: file.size,
        replacesExistingFile: true,
        // An unknown current size is counted as 0, which over-estimates the
        // projected total and refuses rather than under-counts.
        replacedFileSize: size ?? 0,
      });
      if (limitError) {
        setReplaceError(limitError);
        return;
      }
      const base64Content = await fileToBase64(file);
      await saveFile({
        pathParams: { skill },
        params: { path },
        body: {
          base64: base64Content,
          contentType: file.type || "application/octet-stream",
          expectedGeneration: generation,
        },
      });
      toast.success(`Replaced ${path}.`);
    } catch (mutationError) {
      if (mutationError instanceof KnownApiError && mutationError.responseStatus === 409) {
        setReplaceError("This file changed after you opened it. Reload it.");
        return;
      }
      setReplaceError(getMutationErrorMessage(mutationError, "Failed to replace the file."));
    } finally {
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      setIsReplacing(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
        <span>
          Type: <span className="text-foreground">{contentType ?? "unknown"}</span>
        </span>
        <span>
          Size:{" "}
          <span className="text-foreground">{size != null ? formatBytes(size) : "unknown"}</span>
        </span>
      </div>
      {base64 && contentType != null && isImageContentType(contentType) && (
        <img
          src={`data:${contentType};base64,${base64}`}
          alt={path}
          className="max-h-96 max-w-full rounded-md border"
        />
      )}
      {replaceError && <p className="text-sm text-destructive">{replaceError}</p>}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            void handleReplaceFile(file);
          }
        }}
      />
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={isReplacing}>
          {isReplacing ? "Replacing…" : "Replace"}
        </Button>
      </div>
    </div>
  );
}

function AddFileDialog({
  skill,
  skillData,
  limits,
  open,
  onOpenChange,
}: {
  skill: string;
  skillData: McpSkill | null;
  limits: McpSkillLimits | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [targetPath, setTargetPath] = useState("");
  const [pathError, setPathError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { mutateAsync: saveFile, isPending } = saveMcpSkillFileMutation.useMutation();

  function reset() {
    setFile(null);
    setTargetPath("");
    setPathError(null);
    setSubmitError(null);
  }

  function close() {
    onOpenChange(false);
    reset();
  }

  async function handleUpload() {
    setSubmitError(null);
    setPathError(null);
    if (!file) {
      setSubmitError("Choose a file.");
      return;
    }

    const pathValidationError = validateSkillFilePath(targetPath);
    if (pathValidationError) {
      setPathError(pathValidationError);
      return;
    }

    if (skillData && limits) {
      const existing = skillData.files.find((entry) => entry.path === targetPath);
      const limitError = checkUploadAgainstLimits({
        limits,
        currentFileCount: skillData.fileCount,
        currentTotalBytes: skillData.totalBytes,
        newFileSize: file.size,
        replacesExistingFile: Boolean(existing),
        replacedFileSize: existing?.size ?? 0,
      });
      if (limitError) {
        setSubmitError(limitError);
        return;
      }
    }

    try {
      const base64 = await fileToBase64(file);
      await saveFile({
        pathParams: { skill },
        params: { path: targetPath },
        body: {
          base64,
          contentType: file.type || "application/octet-stream",
          expectedGeneration: 0,
        },
      });
      toast.success(`Added ${targetPath}.`);
      close();
    } catch (mutationError) {
      if (mutationError instanceof KnownApiError && mutationError.responseStatus === 409) {
        setSubmitError(
          isEditLockConflict(mutationError)
            ? "Another edit to this skill is in progress. Try again."
            : "A file with this path already exists."
        );
        return;
      }
      setSubmitError(getMutationErrorMessage(mutationError, "Failed to add the file."));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          onOpenChange(true);
        } else {
          close();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add file to {skill}</DialogTitle>
          <DialogDescription>
            The file is sent as base64 in JSON and stored in the bucket.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="add-file-input">File</Label>
            <input
              id="add-file-input"
              type="file"
              className="text-sm"
              onChange={(event) => {
                const selected = event.target.files?.[0] ?? null;
                setFile(selected);
                setSubmitError(null);
                if (selected && !targetPath) {
                  setTargetPath(defaultUploadPath(selected.name));
                }
              }}
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} — {formatBytes(file.size)}
                {file.type ? ` (${file.type})` : ""}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-file-path">Target path</Label>
            <Input
              id="add-file-path"
              value={targetPath}
              onChange={(event) => {
                setTargetPath(event.target.value);
                setPathError(null);
                setSubmitError(null);
              }}
              placeholder="assets/example.png"
              autoComplete="off"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              A relative path inside the skill, for example assets/logo.png. Letters, digits, dots,
              underscores and hyphens only.
            </p>
            {pathError && <p className="text-xs text-destructive">{pathError}</p>}
          </div>
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleUpload()} disabled={isPending || !file}>
            {isPending ? "Adding…" : "Add file"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
